import React from 'react';
import { Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

import TuningAdminTable from '../tuning/TuningAdminTable';
import {
  EntityAliasFields,
  GtmTypeFields,
  ProductOfferTypeFields,
} from '../rag/ragAdminConstants';
import { dateLabel } from '../rag/ragAdminUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_URL_TOOLBOX_CONFIG } from '../../PageStructure/admin-values';

type ConfigTab =
  | 'product-offer-types'
  | 'gtm-types'
  | 'entity-aliases';

const allowedTabs = ['gtm-types', 'entity-aliases'];

const AIConfigView: React.FC = () => {
  const { tab2 } = useParams();
  const navigate = useNavigate();
  const tab = !!tab2 && allowedTabs.includes(tab2) ? tab2 : 'product-offer-types';

  return (
    <Stack spacing={2}>
      <div>tab2: {tab2}</div>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SettingsIcon color="primary" />

          <div>
            <Typography variant="h6" fontWeight={700}>
              AI Configuration
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Manage shared AI reference data used across RAG, tuning, documentation, and routing workflows.
            </Typography>
          </div>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/config/${value}`)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="product-offer-types" label="Product Offer Types" />
          <Tab value="gtm-types" label="GTM Types" />
          <Tab value="entity-aliases" label="Entity Aliases" />
        </Tabs>

        {tab === 'product-offer-types' ? (
          <TuningAdminTable
            title="Product Offer Types"
            description="DB-backed reference data for product.offer_type and product-offer defaults."
            endpoint="db/product-offer-type/"
            queryKey={['product-offer-type']}
            fields={ProductOfferTypeFields}
            defaultValues={{
              key: '',
              label: '',
              description: '',
              is_active: true,
              sort_order: 100,
              updated_by: 'admin-ui',
            }}
            orderBy="sort_order"
            rowTitle={(row) => row.label || row.key}
            rowSubtitle={(row) => row.key}
          />
        ) : null}

        {tab === 'gtm-types' ? (
          <TuningAdminTable
            title="Go-To-Market Types"
            description="DB-backed reference data for go_to_market.type and GTM-type defaults."
            endpoint="db/go-to-market-type/"
            queryKey={['go-to-market-type']}
            fields={GtmTypeFields}
            defaultValues={{
              key: '',
              label: '',
              description: '',
              is_active: true,
              sort_order: 100,
              updated_by: 'admin-ui',
            }}
            orderBy="sort_order"
            rowTitle={(row) => row.label || row.key}
            rowSubtitle={(row) => row.key}
          />
        ) : null}

        {tab === 'entity-aliases' ? (
          <TuningAdminTable
            title="Documentation Entity Type Aliases"
            description="Aliases used to normalize UI/API input into canonical entity types."
            endpoint="db/documentation-entity-type-alias/"
            queryKey={['documentation-entity-type-alias']}
            fields={EntityAliasFields}
            defaultValues={{
              entity_type: '',
              alias: '',
              updated_by: 'admin-ui',
            }}
            orderBy="alias"
            rowTitle={(row) => row.alias}
            rowSubtitle={(row) => `Entity Type ID: ${row.entity_type}`}
            columns={[
              {
                key: 'alias',
                label: 'Alias',
                render: (row) => (
                  <Typography variant="body2" fontWeight={700}>
                    {row.alias}
                  </Typography>
                ),
              },
              {
                key: 'entity_type',
                label: 'Entity Type',
                width: 220,
                render: (row) => row.entity_type_key || row.entity_type,
              },
              {
                key: 'last_updated',
                label: 'Updated',
                width: 180,
                render: (row) => dateLabel(row.last_updated),
              },
            ]}
          />
        ) : null}
      </Paper>
    </Stack>
  );
};

export default AIConfigView;